"use client";

import { m as motion } from "framer-motion";
import { CurriculumSession } from "./curriculum-session";
import { Server } from "lucide-react";

export const CurriculumSection = () => {
  const curriculumSessions = [
    {
      session: 1,
      title: "Understanding your AI Readiness",
      subtitle:
        "The six forces that shape whether AI deployments stick — or stall",
      description:
        "Assess where your organization stands and learn from real HR case studies of adoption that worked — and choices that didn't.",
      outcomes: [
        "Place your org on an AI readiness spectrum and name the biggest gaps",
        "Spot the usual blockers (and early warning signs) before they derail pilots",
        "Compare your situation to patterns from HR/Talent examples — not generic vendor stories",
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
        </svg>
      ),
    },
    {
      session: 2,
      title: "Selecting a Use Case with Task-Tech Fit",
      subtitle: "Five practical filters for picking work where AI is more likely to earn trust and ROI",
      description:
        "Move from \"lots of ideas\" to a short list you can defend with tradeoffs.",
      outcomes: [
        "Apply a task–tech fit lens to candidate use cases in your workflow",
        "Prioritize higher-impact, lower-regret starting points",
        "Draft a concise stakeholder narrative (problem → approach → what \"good\" looks like)",
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
          <path d="m22 2-7 20-4-9-9-4Z" />
          <path d="M22 2 11 13" />
        </svg>
      ),
    },
    {
      session: 3,
      title: "Creating an AI Assistant Proof-of-Concept",
      subtitle: "A simple build path: define the job → constrain the behavior → test with real scenarios",
      description:
        "Ship something demoable, not a science project.",
      outcomes: [
        "Configure a working assistant aligned to your chosen use case",
        "Stress-test outputs against realistic HR scenarios and edge cases",
        "Demo the prototype in a way leadership can understand quickly",
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
          <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
          <path d="M7 7h.01" />
        </svg>
      ),
    },
    {
      session: 4,
      title: "Preparing Knowledge for AI",
      subtitle: "How to extract, clean, and structure what the model can use — so answers are useful, not confidently wrong",
      description:
        "Understand what \"good context\" looks like in practice.",
      outcomes: [
        "Prepare documents and knowledge so they're usable by an assistant",
        "Recognize common failure modes (thin context, stale policy, ambiguous ownership)",
        "Design a lightweight knowledge flow that feeds your prototype",
      ],
      icon: <Server className="w-10 h-10" />,
    },
    {
      session: 5,
      title: "Preparing to Scale",
      subtitle: "Turn a working demo into adoption: audience, messaging, measurement, and sequencing",
      description:
        "Leave with a concrete next-step plan, not a slide of aspirations.",
      outcomes: [
        "Outline a rollout plan: who uses it first, how trust builds, what \"done\" means",
        "Use behavioral science lenses to reduce resistance and increase real usage",
        "Walk away with a prioritized action plan and timeline you can execute",
      ],
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
          <path d="M5 22h14" />
          <path d="M5 2h14" />
          <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
          <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
        </svg>
      ),
    },
  ];

  return (
    <motion.section
      id="curriculum"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="container-class relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-mesh opacity-20 pointer-events-none" />

      <div className="section-inner relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          className="flex flex-col items-center justify-center space-y-4 text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
            The Five Sessions
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl">
            Five sessions. One thread: diagnose → choose → build → ground → scale.
          </p>
        </motion.div>

        {/* Timeline layout */}
        <div className="relative">
          {/* Vertical connector line - desktop only */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-secondary to-accent -translate-x-1/2" />

          <div className="space-y-8 md:space-y-12">
            {curriculumSessions.map((session, index) => (
              <motion.div
                key={session.session}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1 }}
              >
                <CurriculumSession
                  {...session}
                  isLeft={index % 2 === 0}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
};
